// @docs:
//   - docs/core/architecture/SHIPS_PAPERS.md
// Bulk import wizard — publish a documentation folder into a shared library
// in three steps: (1) choose source & scan locally, (2) review the proposed
// manifest, (3) publish with resumable, concurrency-limited uploads against
// the existing single-file endpoints. The sha pre-flight makes re-runs a
// diff: files already on the vessel are attached, not re-uploaded. The
// publishing itself is bulkImportEngine.ts; this file is its React shell.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderOpenIcon, ArrowLeftIcon, ArrowDownTrayIcon, PauseIcon, PlayIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import type { Catalog, PaperLibrary } from '../../types/papers';
import { btnPrimary, btnGhost, input, label as labelCls, errMsg, fmtBytes, Modal } from './shared';
import { PaperLibraryForm } from './SharedPapersPanel';
import { classify, duplicateSuspects, filesFromDataTransfer, reportCsv, sha256HexOf, tagsFor, type Proposal, type ScanFile, type ScanStats } from './bulkImportRules';
import { LIBRARY_ITEM_CAP, runPublish, summarize, type EngineDeps, type Ledger, type RunResult } from './bulkImportEngine';

type Step = 1 | 2 | 3;

const PAGE = 200;
const WAVE = 20;          // items hashed + looked up per wave
const CONCURRENCY = 3;    // uploads in flight

export function BulkImportWizard({ catalog, vessels, defaultVesselId, initialLibraryId, onClose }: {
  catalog: Catalog;
  vessels: Array<{ id: string; name: string; role: string }>;
  defaultVesselId: string;
  /** Preselect the target library (e.g. opened from that library's page). */
  initialLibraryId?: string;
  onClose: () => void;
}) {
  const addToast = useToastStore((s) => s.addToast);
  const qc = useQueryClient();
  const targets = vessels.filter((v) => ['owner', 'captain'].includes(v.role));   // publishing requires own/captain
  const [step, setStep] = useState<Step>(1);
  const [vesselId, setVesselId] = useState(targets.some((v) => v.id === defaultVesselId) ? defaultVesselId : targets[0]?.id ?? '');
  const [libraryId, setLibraryId] = useState<string>(initialLibraryId ?? '');
  const [creatingLib, setCreatingLib] = useState(false);
  const [files, setFiles] = useState<ScanFile[]>([]);
  const [scanning, setScanning] = useState(false);
  const [englishOnly, setEnglishOnly] = useState(true);
  const [excludeCompliance, setExcludeCompliance] = useState(true);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [stats, setStats] = useState<ScanStats | null>(null);
  const [search, setSearch] = useState('');
  const [productF, setProductF] = useState('');
  const [shown, setShown] = useState(PAGE);
  const [dropHot, setDropHot] = useState(false);
  /** The user's own include/exclude ticks — kept when a filter re-classifies. */
  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map());
  /** Content hashes of files that share a size with another, so duplicates group by content. */
  const shaCache = useRef<Map<string, string>>(new Map());
  const [checkingDupes, setCheckingDupes] = useState<{ done: number; total: number } | null>(null);
  // publish state — the ledger is the truth; `version` re-renders when it changes
  const ledger = useRef<Ledger>(new Map());
  const [version, setVersion] = useState(0);
  const [running, setRunning] = useState(false);
  const [started, setStarted] = useState(false);
  const [stopped, setStopped] = useState<RunResult['stopped']>(null);
  const pausedRef = useRef(false);
  const [paused, setPausedState] = useState(false);
  const stopRef = useRef(false);
  /** Every pause goes through here, so the engine (ref) and the button (state) agree. */
  const setPaused = (v: boolean) => { pausedRef.current = v; setPausedState(v); };
  const batchId = useRef(`${Date.now().toString(36)}`);
  const folderInput = useRef<HTMLInputElement>(null);

  const { data: mine } = useQuery({ queryKey: ['paper-lib-mine'], queryFn: () => api.paperLibMine() });
  const equipTypes = useMemo(() => catalog.sections.find((s) => s.key === 'equipment')?.types ?? [{ key: 'other', label: 'Other' }], [catalog]);
  const library = (mine ?? []).find((l) => l.id === libraryId) ?? null;
  const room = Math.max(0, LIBRARY_ITEM_CAP - (library?.itemCount ?? 0));

  // What an earlier run did belongs to that vessel and library; another target starts afresh.
  useEffect(() => { ledger.current = new Map(); setStarted(false); setStopped(null); setVersion((v) => v + 1); }, [vesselId, libraryId]);

  const runClassify = (fs: ScanFile[], eo = englishOnly, ec = excludeCompliance, ov = overrides) => {
    const r = classify(fs, { englishOnly: eo, excludeCompliance: ec, catalogTypes: equipTypes, sha: shaCache.current, overrides: ov });
    setProposals(r.proposals);
    setStats(r.stats);
    setShown(PAGE);
  };

  /** Scan → classify at once, then hash same-size files and classify again with their hashes. */
  const scanned = async (fs: ScanFile[]) => {
    setFiles(fs);
    shaCache.current = new Map();
    const ov = new Map<string, boolean>();
    setOverrides(ov);
    runClassify(fs, englishOnly, excludeCompliance, ov);
    const suspects = new Set(duplicateSuspects(fs));
    if (!suspects.size) return;
    const targets = fs.filter((f) => suspects.has(f.relPath.replace(/\\/g, '/')));
    setCheckingDupes({ done: 0, total: targets.length });
    let n = 0;
    for (const f of targets) {
      try { shaCache.current.set(f.relPath.replace(/\\/g, '/'), await sha256HexOf(f.file)); } catch { /* unreadable: grouped by name instead */ }
      setCheckingDupes({ done: ++n, total: targets.length });
    }
    setCheckingDupes(null);
    runClassify(fs, englishOnly, excludeCompliance, ov);
  };

  const onPickFolder: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const list = Array.from(e.target.files ?? []);
    const fs = list.map((f) => ({ file: f, relPath: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name }));
    e.target.value = '';
    void scanned(fs);
  };
  const onDrop: React.DragEventHandler = async (e) => {
    e.preventDefault();
    setDropHot(false);
    setScanning(true);
    try {
      const fs = await filesFromDataTransfer(e.dataTransfer.items);
      setScanning(false);
      await scanned(fs);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not read that folder', message: errMsg(err, 'Try “Choose folder”.') });
    } finally { setScanning(false); }
  };

  const included = useMemo(() => proposals.filter((p) => p.include && !p.dupOf && !p.oversize), [proposals]);
  const includedBytes = useMemo(() => included.reduce((a, p) => a + p.sizeBytes, 0), [included]);
  const products = useMemo(() => Array.from(new Set(proposals.map((p) => p.product))).sort(), [proposals]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return proposals.filter((p) =>
      (!q || p.title.toLowerCase().includes(q) || p.relPath.toLowerCase().includes(q)) &&
      (!productF || p.product === productF));
  }, [proposals, search, productF]);

  const setInclude = (keys: string[], include: boolean) => {
    const pick = new Set(keys);
    setOverrides((o) => { const n = new Map(o); for (const p of proposals) if (pick.has(p.key) && !p.dupOf && !p.oversize) n.set(p.key, include); return n; });
    setProposals((ps) => ps.map((p) => (pick.has(p.key) && !p.dupOf && !p.oversize ? { ...p, include, excludeReason: include ? null : 'manual' } : p)));
  };

  const saveDraft = () => {
    const draft = { batchId: batchId.current, englishOnly, excludeCompliance, overrides: proposals.map((p) => ({ relPath: p.relPath, include: p.include, title: p.title, product: p.product })) };
    const url = URL.createObjectURL(new Blob([JSON.stringify(draft, null, 1)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url; a.download = `bulk-import-${library?.name ?? 'library'}.draft.json`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  // ---------------- publish (bulkImportEngine.ts) ----------------

  const publish = async () => {
    if (!library || !vesselId) return;
    setStep(3); setRunning(true); setStarted(true); setStopped(null);
    stopRef.current = false; setPaused(false);
    const lib = library;
    const deps: EngineDeps = {
      hash: async (p) => shaCache.current.get(p.key) ?? sha256HexOf(p.file),
      lookup: (shas) => api.papersLookupBySha(vesselId, shas),
      create: (p) => api.papersCreateDocument(vesselId, {
        // Manufacturer documentation is equipment; a legacy typed
        // library dictates its own section, a mixed one doesn't.
        section: lib.section ?? 'equipment', type: p.typeKey, title: p.title, folder: p.product.slice(0, 80),
        tags: tagsFor(p, batchId.current),
        notes: p.mergedProducts.length > 1 ? `Applies to: ${p.mergedProducts.join(', ')}` : null,
      }),
      upload: async (id, p) => { await api.papersUploadFile(vesselId, id, p.file); },
      remove: (id) => api.papersDeleteDocument(vesselId, id),
      attach: (ids) => api.paperLibAddItems(lib.id, ids),
      isPaused: () => pausedRef.current,
      isStopped: () => stopRef.current,
    };
    try {
      // The library's count now, not when the list was last fetched — an earlier run added to it.
      const fresh = await qc.fetchQuery({ queryKey: ['paper-lib-mine'], queryFn: () => api.paperLibMine(), staleTime: 0 }).catch(() => mine ?? []);
      const count = fresh.find((l) => l.id === lib.id)?.itemCount ?? lib.itemCount;
      const r = await runPublish(included, ledger.current, deps, { room: Math.max(0, LIBRARY_ITEM_CAP - count), wave: WAVE, concurrency: CONCURRENCY }, () => setVersion((v) => v + 1));
      setStopped(r.stopped);
    } catch (err) {
      setStopped({ reason: 'cancelled', message: errMsg(err, 'The import stopped unexpectedly. Retry picks up where it left off.') });
    } finally {
      setRunning(false); setPaused(false); setVersion((v) => v + 1);
      for (const k of ['paper-lib', 'paper-lib-mine', 'paper-lib-detail', 'papers-docs', 'papers-doc', 'papers-vessel']) qc.invalidateQueries({ queryKey: [k] });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const counts = useMemo(() => summarize(included, ledger.current), [version, included]);
  const retryable = counts.failed + counts.skipped;

  const downloadReport = () => {
    const rows = included.map((p) => { const s = ledger.current.get(p.key); return { relPath: p.relPath, status: s?.status ?? 'queued', detail: s?.detail ?? '' }; });
    const url = URL.createObjectURL(new Blob([reportCsv(rows)], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'bulk-import-report.csv'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  // ---------------- render ----------------

  const stepChip = (n: Step, text: string) => (
    <span className={`inline-flex items-center gap-1.5 text-[13px] font-bold ${step === n ? 'text-amber-400' : step > n ? 'text-emerald-300' : 'text-slate-500'}`}>
      <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[13px] ${step === n ? 'bg-amber-400 text-slate-900' : step > n ? 'bg-emerald-400/20' : 'border border-slate-600'}`}>{step > n ? '✓' : n}</span>{text}
    </span>
  );

  return (
    <Modal title="Bulk import documents" onClose={running ? () => setPaused(true) : onClose} dirty={!running && !!stats && !started} wide>
      <div className="flex items-center gap-3 flex-wrap text-slate-500">{stepChip(1, 'Choose source')}<span>—</span>{stepChip(2, 'Review manifest')}<span>—</span>{stepChip(3, 'Publish')}</div>

      {step === 1 && (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className={labelCls}>Documents live on vessel
              <select className={`${input} mt-1`} value={vesselId} onChange={(e) => setVesselId(e.target.value)}>
                {targets.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </label>
            <label className={labelCls}>Target shared library
              <select className={`${input} mt-1`} value={libraryId} onChange={(e) => e.target.value === '__new' ? setCreatingLib(true) : setLibraryId(e.target.value)}>
                <option value="">Choose…</option>
                {(mine ?? []).map((l) => <option key={l.id} value={l.id}>{l.name} ({l.itemCount})</option>)}
                <option value="__new">＋ New shared library…</option>
              </select>
            </label>
          </div>
          {targets.length === 0 && <p className="text-sm text-amber-300">Publishing needs a vessel you own or captain.</p>}
          <div
            onDrop={onDrop} onDragOver={(e) => { e.preventDefault(); setDropHot(true); }} onDragLeave={() => setDropHot(false)}
            className={`border-2 border-dashed rounded-xl p-8 text-center ${dropHot ? 'border-amber-400 bg-amber-400/5' : 'border-slate-600 bg-slate-900/40'}`}>
            <FolderOpenIcon className="w-10 h-10 mx-auto text-slate-500" />
            <div className="mt-2 text-base font-bold text-white">Drop a documentation folder</div>
            <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">Scanned on this computer first — nothing uploads until you approve the manifest. PDF and images only, 95 MB per file.</p>
            <button type="button" onClick={() => folderInput.current?.click()} className={`${btnGhost} mt-3`}>Choose folder…</button>
            {/* @ts-expect-error webkitdirectory is non-standard but universal */}
            <input ref={folderInput} type="file" webkitdirectory="" multiple className="hidden" onChange={onPickFolder} />
            {scanning && <div className="text-sm text-slate-400 mt-2" role="status">Scanning…</div>}
            {checkingDupes && <div className="text-sm text-slate-400 mt-2" role="status">Checking {checkingDupes.total.toLocaleString()} files of the same size for duplicates… {checkingDupes.done.toLocaleString()} done</div>}
          </div>
          {stats && (
            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 px-4 py-3 text-sm text-slate-300">
              <b className="text-emerald-300">Scanned:</b> {stats.docFiles.toLocaleString()} documents found ({stats.totalFiles.toLocaleString()} files; {stats.ignoredOther.toLocaleString()} ignored — software, CAD…) · {stats.products.length} products{stats.sidecarCount ? ` · vendor metadata detected (${stats.sidecarCount} sidecars)` : ''}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
            <button type="button" disabled={!stats || !libraryId || !vesselId || !!checkingDupes} onClick={() => setStep(2)} className={btnPrimary}>Review manifest →</button>
          </div>
        </div>
      )}

      {step === 2 && stats && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            {([['Found', String(stats.docFiles)], ['Will publish', `${included.length}`], ['Duplicates merged', String(stats.duplicates)], ['Excluded translations', String(stats.translations)]] as const).map(([l, v]) => (
              <div key={l} className="rounded-lg border border-slate-700 bg-slate-900/50 px-2 py-2"><div className="text-lg font-bold text-white tabular-nums">{v}</div><div className="text-[13px] text-slate-400">{l}</div></div>
            ))}
          </div>
          <div className="text-sm text-slate-300">Publishing <b className="text-white">{included.length.toLocaleString()}</b> documents · <b className="text-white">{fmtBytes(includedBytes)}</b> into <b className="text-amber-300">{library?.name}</b>{stats.oversize > 0 && <span className="text-red-300"> · {stats.oversize} over the 95 MB cap (excluded)</span>}</div>
          {library && included.length > room && (
            <p role="alert" className="text-sm text-amber-200 bg-amber-900/30 border border-amber-700/60 rounded-lg px-3 py-2">
              A shared library holds up to {LIBRARY_ITEM_CAP.toLocaleString()} documents; <b>{library.name}</b> has room for {room.toLocaleString()} more. The import stops when it is full — exclude some, or split them across libraries.
            </p>
          )}
          <div className="flex flex-wrap gap-2 items-center">
            <input type="search" aria-label="Search titles or paths" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search titles or paths…" className={`${input} flex-1 min-w-[180px]`} />
            <label className="inline-flex items-center gap-1.5 text-sm text-slate-300"><input type="checkbox" checked={englishOnly} onChange={(e) => { setEnglishOnly(e.target.checked); runClassify(files, e.target.checked, excludeCompliance); }} className="w-4 h-4 rounded text-amber-400" />English only</label>
            <label className="inline-flex items-center gap-1.5 text-sm text-slate-300"><input type="checkbox" checked={excludeCompliance} onChange={(e) => { setExcludeCompliance(e.target.checked); runClassify(files, englishOnly, e.target.checked); }} className="w-4 h-4 rounded text-amber-400" />Skip compliance certs</label>
            <select aria-label="Product" value={productF} onChange={(e) => setProductF(e.target.value)} className="rounded-lg bg-slate-900 border border-slate-600 text-white text-sm px-2 py-1.5"><option value="">All products</option>{products.map((p) => <option key={p}>{p}</option>)}</select>
            <button type="button" onClick={() => setInclude(visible.map((v) => v.key), true)} className="text-[13px] text-amber-300 hover:underline">include shown</button>
            <button type="button" onClick={() => setInclude(visible.map((v) => v.key), false)} className="text-[13px] text-slate-400 hover:underline">exclude shown</button>
          </div>
          <div className="border border-slate-700 rounded-xl overflow-hidden">
            <div className="max-h-[320px] overflow-y-auto divide-y divide-slate-700/50">
              {visible.slice(0, shown).map((p) => (
                <label key={p.key} className={`flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer ${p.include ? '' : 'opacity-50'}`}>
                  <input type="checkbox" disabled={!!p.dupOf || p.oversize} checked={p.include} onChange={(e) => setInclude([p.key], e.target.checked)} className="w-4 h-4 rounded text-amber-400 shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-white">{p.title}{p.mergedProducts.length > 1 && <span className="ml-1.5 text-[13px] font-bold text-amber-400 border border-amber-400/40 rounded px-1">×{p.mergedProducts.length} models</span>}</span>
                  <span className="text-slate-400 w-28 truncate hidden sm:block">{p.product}</span>
                  <span className="text-slate-500 w-10 tabular-nums">{p.language}</span>
                  <span className="text-slate-500 w-16 text-right tabular-nums">{fmtBytes(p.sizeBytes)}</span>
                  <span className={`w-32 text-right text-[13px] truncate ${p.oversize ? 'text-red-300' : p.excludeReason ? 'text-slate-500' : 'text-emerald-300'}`}>
                    {p.oversize ? 'over 95 MB' : p.dupOf ? 'duplicate of primary' : p.excludeReason ?? 'ready'}
                  </span>
                </label>
              ))}
            </div>
            {visible.length > shown && <button type="button" onClick={() => setShown((n) => n + PAGE)} className="w-full py-2 text-sm text-amber-300 hover:bg-slate-700/40 border-t border-slate-700">Show {Math.min(PAGE, visible.length - shown)} more of {visible.length.toLocaleString()}</button>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setStep(1)} className={btnGhost}><ArrowLeftIcon className="w-4 h-4" /> Back</button>
            <button type="button" onClick={saveDraft} className={btnGhost}><ArrowDownTrayIcon className="w-4 h-4" /> Save draft</button>
            <span className="flex-1" />
            <button type="button" disabled={!included.length} onClick={publish} className={btnPrimary}>Publish {included.length.toLocaleString()} documents</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <div className="flex items-baseline gap-3 flex-wrap">
            <div role="status" aria-live="polite" className="flex items-baseline gap-3 flex-wrap">
              <span className="text-xl font-bold text-white tabular-nums">{counts.published + counts.attached} / {counts.total}</span>
              <span className="text-sm text-slate-400">{fmtBytes(counts.doneBytes)} uploaded{counts.attached ? ` · ${counts.attached} attached without upload (already on the vessel)` : ''}{counts.failed ? ` · ${counts.failed} failed` : ''}{counts.skipped ? ` · ${counts.skipped} not started` : ''}{!running && started && !stopped && !retryable ? ' · finished' : ''}</span>
            </div>
            <span className="flex-1" />
            {running && (
              <button type="button" onClick={() => setPaused(!pausedRef.current)} className={btnGhost}>
                {paused ? <><PlayIcon className="w-4 h-4" /> Resume</> : <><PauseIcon className="w-4 h-4" /> Pause</>}
              </button>
            )}
            {running && paused && <button type="button" onClick={() => { stopRef.current = true; setPaused(false); }} className={btnGhost}>Stop</button>}
          </div>
          <div role="progressbar" aria-label="Import progress" aria-valuemin={0} aria-valuemax={counts.total} aria-valuenow={counts.total - counts.pending}
            className="h-2.5 rounded-full bg-slate-700 overflow-hidden"><div className="h-full bg-amber-400 transition-all" style={{ width: `${counts.total ? Math.round(((counts.total - counts.pending) / counts.total) * 100) : 0}%` }} /></div>
          {stopped && <p role="alert" className="text-sm text-amber-200 bg-amber-900/30 border border-amber-700/60 rounded-lg px-3 py-2">{stopped.message}</p>}
          <div className="border border-slate-700 rounded-xl max-h-[300px] overflow-y-auto divide-y divide-slate-700/50">
            {included.map((p) => {
              const s = ledger.current.get(p.key);
              if (!s || s.status === 'queued') return null;
              return (
                <div key={p.key} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                  {s.status === 'published' || s.status === 'attached' ? <CheckCircleIcon className="w-4 h-4 text-emerald-300 shrink-0" />
                    : s.status === 'failed' ? <ExclamationTriangleIcon className="w-4 h-4 text-red-300 shrink-0" />
                    : s.status === 'skipped' ? <span className="w-4 h-4 shrink-0 rounded-full border-2 border-slate-500" />
                    : <span className="w-4 h-4 shrink-0 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />}
                  <span className="flex-1 min-w-0 truncate text-slate-200">{p.title}</span>
                  <span className={`text-[13px] ${s.status === 'failed' ? 'text-red-300' : 'text-slate-400'}`}>{s.detail ?? s.status}</span>
                </div>
              );
            })}
          </div>
          {paused && running && <p className="text-sm text-amber-300">Paused — nothing is uploading. Resume to continue, or Stop; already-published files are never re-uploaded.</p>}
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[13px] text-slate-400 flex-1">Re-running this import later is safe: files already published are detected by content hash and skipped.</p>
            <button type="button" onClick={downloadReport} className={btnGhost}><ArrowDownTrayIcon className="w-4 h-4" /> Report (CSV)</button>
            {!running && retryable > 0 && <button type="button" onClick={publish} className={btnGhost}>Retry {retryable.toLocaleString()} not published</button>}
            {!running && <button type="button" onClick={onClose} className={btnPrimary}>{retryable > 0 ? 'Close' : 'Done'}</button>}
          </div>
        </div>
      )}

      {creatingLib && (
        <PaperLibraryForm initial={null} catalog={catalog}
          onClose={() => setCreatingLib(false)}
          onSaved={(l: PaperLibrary) => { setCreatingLib(false); qc.invalidateQueries({ queryKey: ['paper-lib-mine'] }); setLibraryId(l.id); }} />
      )}
    </Modal>
  );
}
